import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Server,
  Layout,
  Cloud,
  Database,
  Brain,
  Zap,
  Shield,
  ArrowRight,
} from 'lucide-react';
import { Container, Section, Card, Badge, Button } from '../ui';
import { SERVICES } from '../../constants';
import { useIntersectionObserver } from '../../hooks';

const iconMap: Record<string, React.ElementType> = {
  Sparkles,
  Server,
  Layout,
  Cloud,
  Database,
  Brain,
  Zap,
  Shield,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export const Services: React.FC = () => {
  const { elementRef, isVisible } = useIntersectionObserver({ threshold: 0.1 });

  return (
    <Section background="white" padding="lg" id="services">
      <Container>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="accent" className="mb-4">Our Expertise</Badge>
          <h2 className="heading-lg text-navy-900 mb-4">
            Comprehensive Technology Services
          </h2>
          <p className="text-body text-lg">
            From cutting-edge AI solutions to robust cloud infrastructure, we deliver 
            end-to-end technology services that drive business growth and innovation.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div
          ref={elementRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
        >
          {SERVICES.map((service) => {
            const IconComponent = iconMap[service.icon] || Sparkles;
            
            return (
              <motion.div key={service.id} variants={itemVariants}>
                <Card hover className="h-full group">
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-navy-100 to-accent-50 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-7 h-7 text-navy-700" />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-semibold text-navy-900 mb-3">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-navy-600 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>

                  {/* Features */}
                  {service.features && (
                    <ul className="space-y-2 mb-4">
                      {service.features.slice(0, 3).map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-navy-500">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Link */}
                  <Link
                    to={`/services/${service.id}`}
                    className="inline-flex items-center gap-2 text-accent-600 text-sm font-medium hover:text-accent-700 mt-auto group/link"
                  >
                    Learn more
                    <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link to="/services">
            <Button variant="outline" size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
              View All Services
            </Button>
          </Link>
        </div>
      </Container>
    </Section>
  );
};

